<!--
Author: Alexey Usov (dax@xdax.ru, https://github.com/doubleaxe)
Please don't remove this comment if you use unmodified file
-->
<script setup lang="ts">
import {nextTick, onBeforeMount, ref, unref, watch} from 'vue';
import {injectGameData} from '@/scripts/data';
import {provideBlueprintModel, type BlueprintModel} from '@/scripts/model/store';
import {provideFilter} from '@/scripts/filter';
import {provideSettings} from '@/scripts/settings';
import {mdiArrowLeft, mdiFormatListBulletedType} from '@mdi/js';
import {useAnalytics, loadBlueprint, useBlueprintAnchorSync, useIsMobile} from '@/composables';
import {useTheme} from 'vuetify';

const drawer = ref(true);
const showSummary = ref(false);
const showSummaryCompact = ref(false);
const gameData = injectGameData();
const blueprintModel = ref<BlueprintModel | undefined>();
const settings = ref<ReturnType<typeof provideSettings> | undefined>();
const theme = useTheme();

//fork addition (Stage I): at most 900px wide the app is one screen
//at a time — the board by default, the toolbox and the inspector as
//full-screen steps over it. Above the breakpoint nothing in this
//file changes behaviour: desktop stays the primary layout.
const {isMobile} = useIsMobile();
if(unref(isMobile))
    drawer.value = false;

function toggleSummary() {
    //toggle in curcular way - hidden -> compact -> full
    if(unref(showSummary)) {
        if(unref(showSummaryCompact)) {
            showSummaryCompact.value = false;
        } else {
            showSummary.value = false;
            showSummaryCompact.value = true;
        }
    } else {
        showSummary.value = true;
        showSummaryCompact.value = true;
    }
    const _settings = unref(settings);
    if(_settings) {
        _settings.showSummary = unref(showSummary);
        _settings.showSummaryCompact = unref(showSummaryCompact);
    }
}

onBeforeMount(() => {
    useAnalytics(gameData.gameDescription.name);
    const _blueprintModel = provideBlueprintModel(gameData);
    const filter = provideFilter(gameData);
    const _settings = provideSettings(gameData, _blueprintModel, filter);
    //fork note (Stage I): the mobile toolbox auto-close subscription
    //lives in <drawer-auto-close> (mounted below), NOT here —
    //usePointAndClick's factory injects the settings, and a component
    //cannot inject its own provides, so calling it from this hook
    //threw "settings not instantiated" and dead-locked the boot on
    //the loading spinner.
    //fork addition (Stage H): live `#/b/...` URL anchor — armed below,
    //once any preloaded blueprint has been applied, so the initial sync
    //reflects the loaded plan instead of racing it with an empty board
    const anchorSync = useBlueprintAnchorSync(gameData, _blueprintModel);
    document.title = `daxfb-calculator - calculator/factory planner for "${gameData.gameDescription.description}"`;

    const preloadBlueprint = gameData.preloadBlueprint;
    Promise.resolve()
        .then(() => new Promise((resolve) => { nextTick(() => resolve(undefined)); }))
        .then(() => new Promise((resolve) => {
            if(preloadBlueprint) {
                setTimeout(() => {
                    loadBlueprint(gameData, _blueprintModel, preloadBlueprint.data, preloadBlueprint.name);
                    gameData.initPreloadBlueprint(undefined);
                    resolve(undefined);
                }, 50);
            } else {
                resolve(undefined);
            }
        }))
        .then(() => {
            blueprintModel.value = _blueprintModel;

            //load at start, and don't watch for changes from another tabs
            //(Stage I: on mobile the summary never auto-opens — it is
            //a full-screen step there, opened only on demand)
            showSummary.value = unref(isMobile) ? false : _settings.showSummary;
            showSummaryCompact.value = unref(isMobile) ? false : _settings.showSummaryCompact;
            settings.value = _settings;
            anchorSync.start();
        });
});

watch(() => unref(settings)?.darkTheme, () => {
    theme.global.name.value = unref(settings)?.darkTheme ? 'dark' : 'light';
});
</script>

<template>
    <icon-draggable v-if="blueprintModel" />
    <!-- fork addition (Stage I): mobile toolbox auto-close on factory
         pick; a separate component because it must call
         usePointAndClick from a descendant of the settings provider -->
    <drawer-auto-close v-if="blueprintModel" @close="drawer = false" />
    <v-app v-if="blueprintModel" class="main-window">
        <v-app-bar density="compact">
            <template #prepend>
                <v-app-bar-nav-icon @click.stop="drawer = !drawer" />
            </template>
            <v-app-bar-title>{{ `${blueprintModel?.blueprintName || ''}` }}</v-app-bar-title>
            <!-- fork addition: dataset/game version stamp (Stage F) -->
            <version-stamp />
            <template #append>
                <main-toolbar>
                    <template #append>
                        <tooltip-button
                            class="summary-toggle"
                            tooltip="Toggle Summary"
                            :icon="mdiFormatListBulletedType"
                            @click="toggleSummary()"
                        />
                    </template>
                </main-toolbar>
            </template>
        </v-app-bar>
        <!-- fork change: wider drawer so the toolbox controls and
             factory names have room to breathe -->
        <!-- fork change (Stage I): on mobile the toolbox is a
             full-screen overlay step (temporary, full width, closed
             by default); desktop keeps the permanent 320px drawer -->
        <v-navigation-drawer
            v-model="drawer"
            :permanent="!isMobile"
            :temporary="isMobile"
            :width="isMobile ? '100%' : 320"
        >
            <!-- fork addition (Stage I): the full-screen toolbox
                 needs its own way back — at 100% width there is no
                 scrim left to tap -->
            <div v-if="isMobile" class="drawer-mobile-bar">
                <v-btn variant="text" size="small" :prepend-icon="mdiArrowLeft" @click="drawer = false">
                    Back
                </v-btn>
                <span class="text-subtitle-2 font-weight-bold">Factories</span>
            </div>
            <icon-list-panel />
        </v-navigation-drawer>
        <v-main scrollable>
            <div class="main-area">
                <!-- fork addition: top balance bar (net external IO) -->
                <balance-bar />
                <div class="board-row">
                    <!-- fork fix (desktop regression repair): .board-area
                         is the board's ONLY scroll container and the
                         inspector is its sibling, NOT part of the
                         scrolled content. Pre-fix the surface's scroll
                         box resolved to the .v-main scroller, whose
                         in-flow content included this whole row — so
                         every pan/zoom scrolled the inspector with the
                         board, clipping it or carrying it off-screen
                         entirely behind the summary drawer. -->
                    <div class="board-wrap">
                        <div class="board-area">
                            <blueprint-panel />
                        </div>
                        <!-- fork addition: bottom-right Save/Copy/Share
                             dock — anchored to the board frame, outside
                             the scroller, so it never moves -->
                        <action-dock />
                    </div>
                    <!-- fork addition: right-hand inspector (selection details) -->
                    <inspector-panel />
                </div>
            </div>
        </v-main>
        <!-- fork fix (desktop regression repair): the summary is an
             OVERLAY at every size, never a permanent layout drawer.
             As a permanent drawer it registered layout padding on
             .v-main, so every toggle shoved the balance bar, the
             board and the action dock sideways; as a temporary drawer
             it registers none, so the frame chrome never moves when
             the summary opens or closes. The desktop scrim is
             suppressed in the CSS below, so the board and the
             inspector underneath stay fully live. Mobile keeps its
             scrim (tap-away still closes the summary step there).
             On desktop the OPEN drawer additionally docks against
             the inspector's left edge (see the .summary-drawer rule
             in the global style below), so it never covers the
             inspector either. -->
        <v-navigation-drawer
            v-model="showSummary"
            temporary
            location="right"
            :rail="showSummaryCompact && !isMobile"
            :class="['summary-drawer', showSummaryCompact ? 'summary-no-overflow' : '']"
        >
            <!-- remove completely, so will not recalculate if not shown -->
            <summary-panel v-if="showSummary" :compact="showSummaryCompact" />
        </v-navigation-drawer>
    </v-app>
    <v-overlay
        v-if="!blueprintModel"
        model-value
        persistent
        class="align-center justify-center"
    >
        <v-progress-circular indeterminate color="primary" />
    </v-overlay>
</template>

<style scoped>
.main-window {
    height: 100%;
    width: 100%;
}
/* fork addition: balance bar on top, board + inspector side by side
   below it. The board area is the board's scroll container; the
   inspector sits outside it in the same row, so board panning can
   never move or clip the inspector. */
.main-area {
    display: flex;
    flex-direction: column;
    height: 100%;
}
.board-row {
    display: flex;
    flex-direction: row;
    flex: 1 1 auto;
    min-height: 0;
}
.board-wrap {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
}
.board-area {
    flex: 1 1 auto;
    min-width: 0;
    position: relative;
    /* the board's scroll container: getScrollBox() resolves the
       surface's nearest overflow:auto ancestor, which is now this
       element — the balance bar, the inspector and the action dock
       all live outside it and stay pinned to the frame */
    overflow: auto;
    scrollbar-width: none;
}
.board-area::-webkit-scrollbar {
    display: none;
}
/* fork addition (Stage I): full-screen toolbox Back bar — rendered
   on mobile only (v-if), so the desktop drawer is untouched */
.drawer-mobile-bar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 8px;
    border-bottom: 1px solid rgba(128, 128, 128, 0.35);
}
</style>

<style>
.summary-no-overflow, .summary-no-overflow > * {
    overflow-y: hidden!important;
}
/* fork addition: the board shows no scrollbars at all — navigation is
   drag-pan + zoom. Only the scrollbar chrome is hidden; the scroller
   keeps overflow: auto so it still scrolls programmatically (the
   board's getScrollBox detection and pan math depend on that) */
.v-main__scroller {
    scrollbar-width: none;
}
.v-main__scroller::-webkit-scrollbar {
    display: none;
}
/* fork fix (desktop regression repair): the summary drawer is a
   temporary (overlay) drawer at every size so it can never push the
   main frame; on desktop its scrim is suppressed so the board and
   the inspector underneath stay fully interactive while it floats.
   Safe as a bare selector on desktop: the summary is the only
   temporary drawer there (the toolbox drawer is permanent above
   900px). Mobile keeps the scrim — tap-away closes the summary. */
@media (min-width: 901px) {
    .v-navigation-drawer__scrim {
        display: none;
    }
    /* fork change (summary docking, Philip's call 2026-10-16 —
       "keep both visible"): while OPEN, the summary docks against
       the inspector's left edge instead of floating over it. The
       inspector is a fixed 300px column at the frame's right edge
       in every state (selected, previewed or idle hint), so the
       active drawer's `right` is offset by exactly that column
       width: the summary then overlays the board's right edge and
       its own box sits fully on-screen, abutting the inspector,
       never intersecting it. Gated on .v-navigation-drawer--active:
       while closed the drawer keeps Vuetify's inline right:0 and
       its translateX(110%) still carries it fully off-screen, so
       the hidden state is byte-identical to before. `right` is in
       the drawer's own transition-property list, so the offset
       animates with the slide. Nothing else moves: the drawer is
       an overlay and registers no layout padding at any offset,
       and the app bar / balance bar / inspector / action dock are
       untouched. Desktop only — at ≤900px the summary keeps its
       full-screen step behaviour. */
    .summary-drawer.v-navigation-drawer--active {
        right: 300px!important;
    }
}
/* fork addition (Stage I): phone app bar — the plan title truncates
   instead of squeezing the toolbar, and the summary toggle (a
   desktop side-panel control) is not part of the phone chrome */
@media (max-width: 900px) {
    .v-app-bar-title, .v-toolbar-title__placeholder {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .summary-toggle {
        display: none!important;
    }
}
</style>
